/**
 * Admin: Approval workflows.
 *
 * Lists WorkflowDefinitions + lets admins create / edit / archive. The
 * step editor below the workflow form lets admins build a linear chain
 * (order, name, approver_resolver, optional role/person target). Saved via
 * the backend's `set-steps` action which atomically replaces the chain.
 */

import { ReactNode, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical, Trash2, GripVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NONE,
  extractErr,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────

type Resolver = 'MANAGER' | 'MANAGER_OF_MANAGER' | 'DEPARTMENT_HEAD' | 'ROLE' | 'PERSON'

type WorkflowStep = {
  id?: number
  order: number
  name: string
  approver_resolver: Resolver
  approver_role: number | null
  approver_role_name?: string | null
  approver_person: number | null
  approver_person_name?: string | null
}

type WorkflowDefinition = {
  id: number
  code: string
  name: string
  description: string
  request_type: number | null
  request_type_code: string | null
  is_active: boolean
  steps: WorkflowStep[]
  step_count: number
}

type RequestType = { id: number; code: string; name: string; is_active: boolean }
type Role = { id: number; name: string }
type Person = { id: number; full_name: string }

const RESOLVER_OPTIONS: Array<{ value: Resolver; label: string }> = [
  { value: 'MANAGER', label: 'Requester\'s manager' },
  { value: 'MANAGER_OF_MANAGER', label: 'Skip-level (manager\'s manager)' },
  { value: 'DEPARTMENT_HEAD', label: 'Department head' },
  { value: 'ROLE', label: 'Anyone in a role' },
  { value: 'PERSON', label: 'A specific person' },
]

// ─────────────────── Page ───────────────────

export default function WorkflowsAdmin() {
  const qc = useQueryClient()
  const { confirm, dialog } = useDeleteConfirm()

  const wfQ = useQuery({
    queryKey: ['requests', 'workflows'],
    queryFn: async () => (await api.get<WorkflowDefinition[]>('/workflows/')).data,
  })
  const typesQ = useQuery({
    queryKey: ['requests', 'types'],
    queryFn: async () => (await api.get<RequestType[]>('/request-types/')).data,
  })
  const rolesQ = useQuery({
    queryKey: ['roles'],
    queryFn: async () => (await api.get<Role[]>('/roles/')).data,
  })
  const personsQ = useQuery({
    queryKey: ['hr', 'persons'],
    queryFn: async () => (await api.get<Person[]>('/hr/persons/')).data,
  })

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<WorkflowDefinition | null>(null)

  const saveMut = useMutation({
    mutationFn: async ({ payload, steps }: { payload: Partial<WorkflowDefinition>; steps: WorkflowStep[] }) => {
      let wf: WorkflowDefinition
      if (editing) {
        wf = (await api.patch<WorkflowDefinition>(`/workflows/${editing.id}/`, payload)).data
      } else {
        wf = (await api.post<WorkflowDefinition>('/workflows/', payload)).data
      }
      // Sync steps via the set-steps action.
      await api.put(`/workflows/${wf.id}/set-steps/`, { steps })
      return wf
    },
    onSuccess: () => {
      toast.success(editing ? 'Workflow updated' : 'Workflow created')
      qc.invalidateQueries({ queryKey: ['requests', 'workflows'] })
      setDrawerOpen(false)
      setEditing(null)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/workflows/${id}/`),
    onSuccess: () => {
      toast.success('Workflow deleted')
      qc.invalidateQueries({ queryKey: ['requests', 'workflows'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const workflows = wfQ.data || []
  const types = (typesQ.data || []).filter((t) => t.is_active)

  return (
    <AdminLayout>
      {dialog}

      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs">
            <Link to="/admin/requests" className="text-muted-foreground hover:text-foreground hover:underline">
              ← My Requests
            </Link>
          </div>
          <h2 className="text-2xl font-semibold text-foreground tracking-tight mt-2">Approval Workflows</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Linear approval chains that request types reference. Steps execute in order; each step's approver is
            resolved at submit time based on the rule you pick.
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setDrawerOpen(true) }}>
          <Plus className="h-3 w-3 mr-1" /> New workflow
        </Button>
      </div>

      {wfQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : workflows.length === 0 ? (
        <div className="bg-card rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground text-sm">
          No workflows yet.
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Type binding</Th>
                <Th>Steps</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {workflows.map((w) => (
                <tr key={w.id} className="hover:bg-muted/30">
                  <Td className="font-mono text-xs">{w.code}</Td>
                  <Td className="text-sm font-medium">{w.name}</Td>
                  <Td className="text-xs">
                    {w.request_type_code || <span className="text-muted-foreground">shared</span>}
                  </Td>
                  <Td className="text-xs text-muted-foreground">
                    {w.step_count} step{w.step_count === 1 ? '' : 's'}
                  </Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${w.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                      {w.is_active ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  </Td>
                  <Td className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => { setEditing(w); setDrawerOpen(true) }}>Edit</DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => confirm(`workflow ${w.code}`, () => deleteMut.mutate(w.id))}
                        >Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {drawerOpen && (
        <WorkflowDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          initial={editing}
          types={types}
          roles={rolesQ.data || []}
          persons={personsQ.data || []}
          onSubmit={(payload, steps) => saveMut.mutate({ payload, steps })}
          isPending={saveMut.isPending}
        />
      )}
    </AdminLayout>
  )
}

// ─────────────────── Drawer ───────────────────

function WorkflowDrawer({
  open, onOpenChange, initial, types, roles, persons, onSubmit, isPending,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  initial: WorkflowDefinition | null
  types: RequestType[]
  roles: Role[]
  persons: Person[]
  onSubmit: (payload: Partial<WorkflowDefinition>, steps: WorkflowStep[]) => void
  isPending: boolean
}) {
  const [code, setCode] = useState(initial?.code || '')
  const [name, setName] = useState(initial?.name || '')
  const [description, setDescription] = useState(initial?.description || '')
  const [requestTypeId, setRequestTypeId] = useState<string>(
    initial?.request_type ? String(initial.request_type) : '',
  )
  const [isActive, setIsActive] = useState(initial?.is_active ?? true)
  const [steps, setSteps] = useState<WorkflowStep[]>(initial?.steps || [])

  useEffect(() => {
    if (!open) return
    setCode(initial?.code || '')
    setName(initial?.name || '')
    setDescription(initial?.description || '')
    setRequestTypeId(initial?.request_type ? String(initial.request_type) : '')
    setIsActive(initial?.is_active ?? true)
    setSteps((initial?.steps || []).map((s) => ({ ...s })))
  }, [open, initial])

  function updateStep(i: number, patch: Partial<WorkflowStep>) {
    setSteps((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)))
  }
  function addStep() {
    setSteps((prev) => [
      ...prev,
      {
        order: prev.length + 1,
        name: `Step ${prev.length + 1}`,
        approver_resolver: 'MANAGER',
        approver_role: null,
        approver_person: null,
      },
    ])
  }
  function removeStep(i: number) {
    setSteps((prev) => prev.filter((_, idx) => idx !== i))
  }
  function moveStep(i: number, dir: -1 | 1) {
    setSteps((prev) => {
      const target = i + dir
      if (target < 0 || target >= prev.length) return prev
      const next = prev.slice()
      ;[next[i], next[target]] = [next[target], next[i]]
      return next
    })
  }

  function submit() {
    if (!code.trim() || !name.trim()) {
      toast.error('Code and name are required.')
      return
    }
    if (steps.length === 0) {
      toast.error('At least one step is required.')
      return
    }
    for (const s of steps) {
      if (s.approver_resolver === 'ROLE' && !s.approver_role) {
        toast.error(`Step "${s.name}" requires a role.`); return
      }
      if (s.approver_resolver === 'PERSON' && !s.approver_person) {
        toast.error(`Step "${s.name}" requires a person.`); return
      }
    }
    onSubmit(
      {
        code: code.trim().toUpperCase(),
        name: name.trim(),
        description,
        request_type: requestTypeId ? Number(requestTypeId) : null,
        is_active: isActive,
      },
      // The backend `set-steps` action assigns order from list position.
      steps.map((s, i) => ({ ...s, order: i + 1 })),
    )
  }

  return (
    <RecordDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={initial ? `Edit workflow — ${initial.name}` : 'New workflow'}
      description="Build a linear approval chain. Each step's approver is resolved at submit time using the rule you pick."
      onSubmit={submit}
      isPending={isPending}
      submitLabel={initial ? 'Save changes' : 'Create workflow'}
      width="wide"
    >
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="wf-code">Code *</FieldLabel>
          <Input
            id="wf-code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="EXPENSE_2_STEP"
            disabled={!!initial}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="wf-name">Name *</FieldLabel>
          <Input id="wf-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field>
          <FieldLabel htmlFor="wf-desc">Description</FieldLabel>
          <Textarea
            id="wf-desc" rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="wf-type">Bound to type</FieldLabel>
          <SearchableSelect
            id="wf-type"
            value={requestTypeId || NONE}
            onChange={(v) => setRequestTypeId(v === NONE ? '' : v)}
            placeholder="— shared (any type can reference) —"
            options={[
              { value: NONE, label: '— shared (any type) —' },
              ...types.map((t) => ({ value: String(t.id), label: t.name, hint: t.code })),
            ]}
          />
          <FieldDescription>
            Optional. If set, this workflow shows only when configuring that specific type.
          </FieldDescription>
        </Field>
        <Field>
          <FieldLabel>Active</FieldLabel>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            Available for new requests
          </label>
        </Field>

        <Field>
          <FieldLabel>Steps</FieldLabel>
          <FieldDescription>
            Run in order from top to bottom. Each step's approver is resolved when the request reaches that step.
          </FieldDescription>
          <div className="space-y-2 mt-2">
            {steps.length === 0 && (
              <p className="text-xs text-muted-foreground">No steps yet. Add at least one.</p>
            )}
            {steps.map((s, i) => (
              <StepEditor
                key={i}
                index={i}
                step={s}
                roles={roles}
                persons={persons}
                onChange={(patch) => updateStep(i, patch)}
                onRemove={() => removeStep(i)}
                onUp={() => moveStep(i, -1)}
                onDown={() => moveStep(i, 1)}
              />
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addStep}>
              <Plus className="h-3 w-3 mr-1" /> Add step
            </Button>
          </div>
        </Field>
      </FieldGroup>
    </RecordDrawer>
  )
}

function StepEditor({
  index, step, roles, persons, onChange, onRemove, onUp, onDown,
}: {
  index: number
  step: WorkflowStep
  roles: Role[]
  persons: Person[]
  onChange: (patch: Partial<WorkflowStep>) => void
  onRemove: () => void
  onUp: () => void
  onDown: () => void
}) {
  return (
    <div className="bg-muted/30 rounded-lg p-3 border border-border">
      <div className="flex items-center gap-2 mb-2">
        <GripVertical className="h-3 w-3 text-muted-foreground" />
        <div className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center shrink-0">
          {index + 1}
        </div>
        <Input
          placeholder="Step name (e.g. Manager approval)"
          value={step.name}
          onChange={(e) => onChange({ name: e.target.value })}
          className="flex-1"
        />
        <button type="button" onClick={onUp} className="p-1 hover:bg-muted rounded text-muted-foreground text-xs">↑</button>
        <button type="button" onClick={onDown} className="p-1 hover:bg-muted rounded text-muted-foreground text-xs">↓</button>
        <button type="button" onClick={onRemove} className="p-1 hover:bg-destructive/10 rounded text-destructive">
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Select
          value={step.approver_resolver}
          onValueChange={(v) => onChange({ approver_resolver: v as Resolver, approver_role: null, approver_person: null })}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {RESOLVER_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {step.approver_resolver === 'ROLE' && (
          <SearchableSelect
            value={step.approver_role ? String(step.approver_role) : NONE}
            onChange={(v) => onChange({ approver_role: v === NONE ? null : Number(v) })}
            placeholder="— pick role —"
            options={[
              { value: NONE, label: '— pick role —' },
              ...roles.map((r) => ({ value: String(r.id), label: r.name })),
            ]}
          />
        )}
        {step.approver_resolver === 'PERSON' && (
          <SearchableSelect
            value={step.approver_person ? String(step.approver_person) : NONE}
            onChange={(v) => onChange({ approver_person: v === NONE ? null : Number(v) })}
            placeholder="— pick person —"
            options={[
              { value: NONE, label: '— pick person —' },
              ...persons.map((p) => ({ value: String(p.id), label: p.full_name })),
            ]}
          />
        )}
      </div>
    </div>
  )
}

// ─────────────────── Helpers ───────────────────

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return (
    <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>
      {children}
    </th>
  )
}

function Td({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>
}
